/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable consistent-return */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/**
 * Copyright (c) 2021, Ouster, Inc.
 * All rights reserved.
 */
import type { Store } from 'easy-peasy';
import { memoize, delay } from 'lodash';

import type { State } from '../reducers';
import { BASE_ORG_API, BASE_PUBLISHED_RECORDING_API } from './constants';

let store: Store<State>;
let refreshAuthToken: (() => Promise<void>) | null = null;
let authRefreshInFlight: Promise<void> | null = null;

export const registerStoreForAuth = (storeToRegister: Store<State>) => {
  store = storeToRegister;
};

export const registerAuthTokenRefresher = (
  refresher: (() => Promise<void>) | null
) => {
  refreshAuthToken = refresher;
};

type CustomRequestInit = RequestInit & {
  fallbackErrorMessage?: string; // Make errorMessage optional
};

const shouldAttachAuthHeader = (url: string) =>
  !url.startsWith(BASE_PUBLISHED_RECORDING_API);

const buildAuthHeaders = (url: string, headers?: HeadersInit) => {
  const mergedHeaders = new Headers(headers);
  const { isAuthenticated, user } = store.getState().user;

  if (shouldAttachAuthHeader(url) && isAuthenticated && user?.accessToken) {
    mergedHeaders.set('Authorization', `Bearer ${user.accessToken}`);
    if (!mergedHeaders.has('Content-Type')) {
      mergedHeaders.set('Content-Type', 'application/json');
    }
  }

  return mergedHeaders;
};

const refreshAuthTokenOnce = async () => {
  if (!refreshAuthToken) return false;

  if (!authRefreshInFlight) {
    authRefreshInFlight = refreshAuthToken().finally(() => {
      authRefreshInFlight = null;
    });
  }

  try {
    await authRefreshInFlight;
    return true;
  } catch {
    return false;
  }
};

const fetchWithAuthRetry = async (
  url: string,
  fetchOpts: RequestInit = {},
  retryOn401 = true
): Promise<Response> => {
  const resp = await fetch(url, {
    ...fetchOpts,
    headers: buildAuthHeaders(url, fetchOpts.headers),
  });

  if (
    resp.status === 401 &&
    retryOn401 &&
    shouldAttachAuthHeader(url) &&
    !fetchOpts.signal?.aborted &&
    (await refreshAuthTokenOnce())
  ) {
    return fetchWithAuthRetry(url, fetchOpts, false);
  }

  return resp;
};

// TODO [vivian] - deprecate this fetch method with the V2 version
/**
 *
 * @param url
 * @param fetchOpts
 */
export function fetchWithAuth(
  url: string,
  fetchOpts: RequestInit = {}
): Promise<Response> {
  return fetchWithAuthRetry(url, fetchOpts);
}

/**
 * Performs a fetch request with authentication headers.
 * @param {string} path - The URL path for the request.
 * @param {CustomRequestInit} [fetchOpts={}] - Additional fetch options:
 * method - (e.g., 'GET', 'POST', 'PUT', 'DELETE')
 * body - The body of the request (optional)
 * @returns {Promise<any>} - A Promise that resolves with the response data (
 For delete requests it doesn't return anything and otherwise it returns the JSON data.)
 * @throws {Error} - If the request fails or returns an error response.
 */

/**
 *
 * @param path
 * @param fetchOpts
 */
export async function fetchWithAuthV2(
  path: string,
  fetchOpts: CustomRequestInit = {}
) {
  const options: RequestInit = {
    ...fetchOpts, // Merge provided fetch options
    headers: buildAuthHeaders(path, {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(fetchOpts.headers || {}), // Merge custom headers if provided
    }),
    body: fetchOpts.body,
  };

  try {
    const resp = await fetchWithAuthRetry(path, options);

    if (options.method === 'DELETE') {
      // For DELETE requests, return if response is okay without attempting to parse JSON
      if (resp.ok) {
        // don't need to parse json
        return;
      }

      let payload: unknown;
      try {
        payload = await resp.json();
      } catch {
        payload = undefined;
      }

      const json = payload as {
        reason?: string;
        error?: string;
        data?: { error?: string };
      };

      throw new Error(
        json?.reason ||
          json?.data?.error ||
          json.error ||
          fetchOpts.fallbackErrorMessage ||
          'An unexpected error!'
      );
    } else {
      // in case the response is not in json format
      const text = await resp.text();
      let json;
      try {
        json = JSON.parse(text);
      } catch (error) {
        if (!resp.ok) {
          throw new Error(
            text || fetchOpts.fallbackErrorMessage || 'An unexpected error!'
          );
        } else {
          // If the response is not JSON but the request was successful
          throw new Error('Received non-JSON response');
        }
      }

      if (!resp.ok) {
        throw new Error(
          json.reason ||
            json.data?.error ||
            json.error ||
            fetchOpts.fallbackErrorMessage ||
            'An unexpected error!'
        );
      }

      return json.data || json;
    }
  } catch (err) {
    throw new Error(
      err instanceof Error
        ? err.message
        : fetchOpts.fallbackErrorMessage || 'An unexpected error!'
    );
  }
}

/* Function that gets a signed URL for specific path.

  We do use memoize here so we do not request a signing on every
  request. Remember that we are making tons of request into each file
  and carve it out via Range headers.

  filePathRelativeToDriveDir is defined as a path to the file to the file from
  the base of the drive directory

  TODO: [Tomas] memoize takes only one parameter by default. Investigate
  if we can use all keys. If not it is still a problem since the files
  names might be the same in multiple drives.
*/
export const getSignedUrl = memoize(
  async (
    filePathRelativeToDriveDir: string,
    driveId: number | string,
    orgId?: number,
    isPrivate = true
  ): Promise<string> => {
    const signingsEndpoint = isPrivate
      ? `${BASE_ORG_API}/${orgId}/recordings/${driveId}/signings/`
      : `${BASE_PUBLISHED_RECORDING_API}/${driveId}/signings/`;
    const osfSignedUrlResponse = await fetchWithAuth(signingsEndpoint, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        object_id: filePathRelativeToDriveDir,
      }),
    });
    if (!osfSignedUrlResponse.ok) {
      throw new Error(
        `Bad response for request for signing of ${filePathRelativeToDriveDir}`
      );
    }
    const osfSignedJSON = await osfSignedUrlResponse.json();
    return osfSignedJSON.url;
  },
  (filePathRelativeToDriveDir, driveId, orgId, isPrivate) =>
    `${String(driveId)}:${String(orgId)}:${String(
      isPrivate
    )}:${filePathRelativeToDriveDir}`
);

// Each signed URL has to have an expiry date If it is say an hour and you have
// your browser opened for two the viz would stop working since the URL is
// expired but new one will not be requested because it ie memoized. For that
// reason we are adding a clean up of the cache every 30 mins The URLs should be
// valid for about an hour. We try to keep the shorter ro limit the
// attack surface
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function resetCache(memoizedFunction: any) {
  memoizedFunction.cache = new memoize.Cache();
  delay(resetCache, 1800, memoizedFunction);
}

delay(resetCache, 1800, getSignedUrl);
